import React, { useState, useEffect, useRef } from "react";
import { cn } from "@/lib/utils";
import { Button } from "./button";
import { Input } from "./input";
import { Card } from "./card";
import { Progress } from "./progress";
import { Upload, X, Image as ImageIcon } from "lucide-react";
import { uploadImage } from "@/services/shared/media/media.service";
import { getImageUrl } from "@/lib/media";

export interface ImageSelectorProps {
  name?: string;
  defaultValue?: string;
  onChange?: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
}

const ImageSelector = React.forwardRef<HTMLInputElement, ImageSelectorProps>(
  (
    {
      name,
      defaultValue = "",
      onChange,
      className,
      placeholder = "URL or upload an image",
      disabled = false,
      ...props
    },
    ref
  ) => {
    const [imageValue, setImageValue] = useState(defaultValue); // Can be URL or filename
    const [previewUrl, setPreviewUrl] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [uploadProgress, setUploadProgress] = useState(0);

    const fileInputRef = useRef<HTMLInputElement>(null);
    const textInputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
      if (defaultValue) {
        setImageValue(defaultValue);
      }
    }, [defaultValue]);

    useEffect(() => {
      const url = getImageUrl(imageValue);
      setPreviewUrl(url);
      
      // Disparar evento change no formulário quando imageValue mudar (após upload)
      // Isso garante que o AutoSaveForm capture o FormData atualizado
      if (imageValue && textInputRef.current) {
        setTimeout(() => {
          const textInput = textInputRef.current;
          if (textInput) {
            // Disparar eventos no input e no formulário
            const inputEvent = new Event('input', { bubbles: true });
            textInput.dispatchEvent(inputEvent);
            
            const changeEvent = new Event('change', { bubbles: true });
            textInput.dispatchEvent(changeEvent);
            
            // Disparar também no formulário
            const form = textInput.closest('form');
            if (form) {
              const formChangeEvent = new Event('change', { bubbles: true });
              form.dispatchEvent(formChangeEvent);
            }
          }
        }, 150);
      }
    }, [imageValue, name]);

    const handleUrlChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      setImageValue(value);
      setError(null);
      if (onChange) {
        onChange(value);
      }
    };

    const handleClearImage = () => {
      setImageValue("");
      setPreviewUrl("");
      setError(null);
      setUploadProgress(0);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
      if (onChange) {
        onChange("");
      }
    };

    const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file) return;

      setError(null);
      setIsLoading(true);
      setUploadProgress(0);

      try {
        const response = await uploadImage(file, setUploadProgress);
        setImageValue(response.image);
        
        if (onChange) {
          onChange(response.image);
        }
        
        // O useEffect vai disparar os eventos quando imageValue mudar
        // Não precisamos disparar aqui, pois o useEffect já faz isso
      } catch (err) {
        setError("Upload failed. Please try again.");
        console.error(err);
      } finally {
        setIsLoading(false);
        setUploadProgress(0);
      }
    };
    
    const triggerFileSelect = () => fileInputRef.current?.click();

    return (
      <div className={cn("w-full space-y-4", className)}>
        <Card className="p-4">
          <div className="flex items-center gap-4">
            <div
              className={cn(
                "w-28 h-28 rounded-lg flex items-center justify-center bg-neutral-100 border border-dashed",
                { "cursor-pointer": !disabled }
              )}
              onClick={!disabled ? triggerFileSelect : undefined}
            >
              {previewUrl ? (
                <img
                  src={previewUrl}
                  alt="Preview"
                  className="w-full h-full object-cover rounded-lg"
                  onError={() => {
                    setError("Failed to load preview");
                    setPreviewUrl("");
                  }}
                />
              ) : (
                <ImageIcon className="h-8 w-8 text-neutral-400" />
              )}
            </div>
            <div className="flex-1 space-y-2">
              <div className="flex gap-2">
                <Input
                  ref={(node) => {
                    textInputRef.current = node;
                    if (typeof ref === 'function') {
                      ref(node);
                    } else if (ref) {
                      ref.current = node;
                    }
                  }}
                  name={name}
                  type="text"
                  value={imageValue}
                  onChange={handleUrlChange}
                  placeholder={placeholder}
                  disabled={disabled}
                  className="flex-1"
                  {...props}
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={triggerFileSelect}
                  disabled={disabled || isLoading}
                  className="px-3"
                >
                  <Upload className="h-4 w-4" />
                </Button>
                {imageValue && (
                  <Button
                    type="button"
                    variant="destructive"
                    size="icon"
                    onClick={handleClearImage}
                    disabled={disabled}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                )}
              </div>
              <p className="text-xs text-neutral-500">
                Enter an image URL or upload a file.
              </p>
            </div>
          </div>

          {isLoading && (
            <div className="mt-2 space-y-1">
              <div className="flex justify-between text-xs text-gray-600">
                <span>Uploading...</span>
                <span>{uploadProgress}%</span>
              </div>
              <Progress value={uploadProgress} className="w-full" />
            </div>
          )}
          
          {error && (
            <div className="mt-2 text-sm text-red-600 bg-red-50 p-2 rounded-md">
              {error}
            </div>
          )}

          <input
            type="file"
            ref={fileInputRef}
            onChange={handleFileChange}
            className="hidden"
            accept="image/png, image/jpeg, image/gif, image/webp"
            disabled={disabled || isLoading}
            aria-label="Upload image file"
          />
        </Card>
      </div>
    );
  }
);

ImageSelector.displayName = "ImageSelector";

export { ImageSelector };
